import { Button, Input } from '@vrplatform/components';
import { showErrorNotification } from '@vrplatform/theme';
import { Box, Modal, Text } from '@mantine/core';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { validateEmail } from '../../../utils';

type Props = {
  opened: boolean;
  close: () => void;
  email: string;
  onSubmit: (email: string) => Promise<{ ok: boolean }>;
};

type FormValues = {
  missingEmail: string;
};

export const ForgotPasswordModal = ({
  close,
  opened,
  onSubmit,
  email,
}: Props) => {
  const methods = useForm<FormValues>({
    defaultValues: { missingEmail: email },
  });

  useEffect(() => {
    if (!opened) {
      setTimeout(() => {
        methods.reset();
      }, 300);
    }
  }, [opened, methods.reset]);

  useEffect(() => {
    methods.setValue('missingEmail', email?.trim() || '');
  }, [email, methods.setValue]);

  const handleSuccess = async (data: FormValues) => {
    const { ok } = await onSubmit(data.missingEmail);
    if (ok) close();
  };

  const handleError = () =>
    showErrorNotification({
      title: 'Incorrect Email',
      message: 'Please enter a valid email address.',
    });

  return (
    <Modal
      opened={opened}
      onClose={close}
      size={500}
      title="Forgot your password?"
      radius="md"
      styles={{
        inner: {
          paddingTop: '25dvh!important',
        },
        body: {
          padding: 20,
          paddingTop: 'initial',
        },
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Text component="p" size="sm" mb="xl" color="neutral">
        Enter your email address and we will send you a magic link to login and
        reset your password.
      </Text>
      <Text component="span" display="block" size="sm" mb={5}>
        Email
      </Text>
      <Controller
        name="missingEmail"
        control={methods.control}
        rules={{
          validate: (value) => validateEmail(value, { required: true }),
        }}
        render={({ field }) => (
          <Input
            {...field}
            data-testid="password-reset-email-input"
            type="email"
            placeholder={`support@${window.location?.host}`}
            mb="xl"
          />
        )}
      />

      <Box>
        <Button
          variant="primary"
          data-testid="password-reset-email-submit"
          sx={(theme) => ({
            width: '100%',
            borderRadius: theme.radius.md,
          })}
          onClick={methods.handleSubmit(handleSuccess, handleError)}
          disabled={!methods.formState.isValid}
        >
          Send Email
        </Button>
      </Box>
    </Modal>
  );
};
